import { useState, useEffect } from 'react';
import {
  MessageCircle, Mail, Send, AlertTriangle, CheckCircle,
  XCircle, Info, ChevronRight, LayoutTemplate, Eye,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { leadService, templateService } from '../../services/api';
import Modal from '../../components/ui/Modal';

const CHANNELS = [
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: '#25D366' },
  { key: 'email',    label: 'Email',    icon: Mail,          color: '#3B82F6' },
];

// ── Step constants ─────────────────────────────────────────────────────────────
const STEP_CHANNEL  = 'channel';
const STEP_TEMPLATE = 'template';
const STEP_PREVIEW  = 'preview';
const STEP_RESULT   = 'result';

const ConnectLeadModal = ({ open, onClose, lead }) => {
  const { dark } = useTheme();

  const [step, setStep]               = useState(STEP_CHANNEL);
  const [channel, setChannel]         = useState('whatsapp');
  const [templates, setTemplates]     = useState([]);
  const [tmplLoading, setTmplLoading] = useState(false);
  const [selected, setSelected]       = useState(null); // chosen template object
  const [preview, setPreview]         = useState(null); // { body, subject }
  const [prevLoading, setPrevLoading] = useState(false);
  const [sending, setSending]         = useState(false);
  const [result, setResult]           = useState(null); // { ok, msg }

  // Reset when modal opens/closes
  useEffect(() => {
    if (!open) {
      setStep(STEP_CHANNEL);
      setChannel('whatsapp');
      setTemplates([]);
      setSelected(null);
      setPreview(null);
      setResult(null);
    }
  }, [open]);

  if (!lead) return null;

  const hasWhatsApp = Boolean(lead.mobile);
  const hasEmail    = Boolean(lead.email);
  const canUse      = channel === 'whatsapp' ? hasWhatsApp : hasEmail;

  // ── Step handlers ───────────────────────────────────────────────────────────

  const goToTemplates = async () => {
    setTmplLoading(true);
    try {
      const { data } = await templateService.getAll({ type: channel });
      setTemplates(data.data || data || []);
    } catch {
      setTemplates([]);
    } finally {
      setTmplLoading(false);
    }
    setSelected(null);
    setStep(STEP_TEMPLATE);
  };

  const goToPreview = async (tmpl) => {
    setSelected(tmpl);
    setPrevLoading(true);
    setStep(STEP_PREVIEW);
    try {
      const { data } = await templateService.preview(tmpl._id, lead._id);
      setPreview(data.data || data);
    } catch {
      setPreview({ body: tmpl.body, subject: tmpl.subject });
    } finally {
      setPrevLoading(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await leadService.connect(lead._id, channel, selected?._id);
      setResult({ ok: true, msg: channel === 'whatsapp' ? 'WhatsApp message sent!' : 'Email sent successfully!' });
    } catch (err) {
      const d = err.response?.data;
      const code = d?.errors?.code || '';
      setResult({
        ok: false,
        code,
        msg: code === 'NOT_ON_WHATSAPP'
          ? 'This number is not registered on WhatsApp.'
          : d?.message || 'Failed to send. Please try again.',
      });
    } finally {
      setSending(false);
      setStep(STEP_RESULT);
    }
  };

  const handleClose = () => {
    setStep(STEP_CHANNEL);
    setSelected(null);
    setPreview(null);
    setResult(null);
    onClose();
  };

  // ── Theme shortcuts ─────────────────────────────────────────────────────────
  const bg     = dark ? '#1E293B' : '#fff';
  const text   = dark ? '#F1F5F9' : '#0F172A';
  const sub    = dark ? '#94A3B8' : '#64748B';
  const border = dark ? '#334155' : '#E2E8F0';
  const surfBg = dark ? '#0F172A' : '#F8FAFC';
  const ch     = CHANNELS.find(c => c.key === channel);

  // ── Step breadcrumb ─────────────────────────────────────────────────────────
  const STEPS = [STEP_CHANNEL, STEP_TEMPLATE, STEP_PREVIEW];
  const stepLabels = { [STEP_CHANNEL]: 'Channel', [STEP_TEMPLATE]: 'Template', [STEP_PREVIEW]: 'Preview & Send' };

  return (
    <Modal open={open} onClose={handleClose} title="Connect with Lead" width={600}>
      {/* Lead strip */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
        background: surfBg, border: `1px solid ${border}`, borderRadius: 10, marginBottom: 20,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(135deg,#3B82F6,#6366F1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, fontWeight: 700, color: '#fff',
        }}>
          {(lead.fullName || '?')[0].toUpperCase()}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: text }}>{lead.fullName}</div>
          <div style={{ fontSize: 12, color: sub, marginTop: 2 }}>
            {lead.mobile || <em style={{ color: '#EF4444' }}>No mobile</em>}
            {lead.email
              ? <span style={{ marginLeft: 10 }}>{lead.email}</span>
              : <em style={{ color: '#EF4444', marginLeft: 10 }}>No email</em>}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: sub, fontFamily: 'monospace' }}>{lead.leadId}</div>
      </div>

      {/* Breadcrumb (hide on result) */}
      {step !== STEP_RESULT && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 20 }}>
          {STEPS.map((s, i) => {
            const idx    = STEPS.indexOf(step);
            const isPast = i < idx;
            const isNow  = i === idx;
            return (
              <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button
                  onClick={() => isPast && setStep(s)}
                  style={{
                    fontSize: 12, fontWeight: isNow ? 700 : 500, border: 'none', background: 'none',
                    cursor: isPast ? 'pointer' : 'default',
                    color: isNow ? (dark ? '#93C5FD' : '#2563EB')
                      : isPast ? sub : dark ? '#475569' : '#CBD5E1',
                    padding: 0, textDecoration: isPast ? 'underline' : 'none',
                  }}
                >
                  {i + 1}. {stepLabels[s]}
                </button>
                {i < STEPS.length - 1 && <ChevronRight size={12} color={dark ? '#475569' : '#CBD5E1'} />}
              </span>
            );
          })}
        </div>
      )}

      {/* ── STEP 1: Channel ────────────────────────────────────────────────── */}
      {step === STEP_CHANNEL && (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
            {CHANNELS.map(c => {
              const available = c.key === 'whatsapp' ? hasWhatsApp : hasEmail;
              const isActive  = channel === c.key;
              const Icon = c.icon;
              return (
                <button
                  key={c.key}
                  onClick={() => available && setChannel(c.key)}
                  title={!available ? `Lead has no ${c.key === 'whatsapp' ? 'mobile' : 'email'}` : ''}
                  style={{
                    flex: 1, padding: '18px 12px', borderRadius: 12, cursor: available ? 'pointer' : 'not-allowed',
                    border: `2px solid ${isActive ? c.color : border}`,
                    background: isActive ? (dark ? c.color + '18' : c.color + '0F') : surfBg,
                    opacity: available ? 1 : 0.4, transition: 'all 0.15s',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  }}
                >
                  <Icon size={26} color={isActive ? c.color : sub} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: isActive ? c.color : sub }}>{c.label}</span>
                  {!available && <span style={{ fontSize: 11, color: '#EF4444' }}>Not available</span>}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={goToTemplates}
              disabled={!canUse}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 22px', borderRadius: 9, border: 'none',
                background: canUse ? ch?.color : (dark ? '#334155' : '#E2E8F0'),
                color: canUse ? '#fff' : sub,
                fontSize: 14, fontWeight: 600, cursor: canUse ? 'pointer' : 'not-allowed',
              }}
            >
              Choose Template <ChevronRight size={15} />
            </button>
          </div>
        </>
      )}

      {/* ── STEP 2: Template picker ─────────────────────────────────────────── */}
      {step === STEP_TEMPLATE && (
        <>
          <div style={{ fontSize: 13, color: sub, marginBottom: 14 }}>
            Select a {ch?.label} template to send to <strong style={{ color: text }}>{lead.fullName}</strong>.
          </div>

          {tmplLoading ? (
            <div style={{ padding: '30px 0', textAlign: 'center', color: sub, fontSize: 13 }}>Loading templates…</div>
          ) : templates.length === 0 ? (
            <div style={{
              padding: '30px 20px', textAlign: 'center', borderRadius: 12,
              border: `1px dashed ${border}`, marginBottom: 20,
            }}>
              <LayoutTemplate size={32} color={sub} style={{ marginBottom: 8 }} />
              <div style={{ fontSize: 14, fontWeight: 600, color: text, marginBottom: 4 }}>No templates found</div>
              <div style={{ fontSize: 12, color: sub }}>
                Go to <strong>Templates</strong> in the sidebar to create {ch?.label} templates.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto', marginBottom: 20 }}>
              {templates.map(tmpl => (
                <button
                  key={tmpl._id}
                  onClick={() => goToPreview(tmpl)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 14px', borderRadius: 10, border: `1px solid ${border}`,
                    background: surfBg, cursor: 'pointer', textAlign: 'left',
                    transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = ch?.color}
                  onMouseLeave={e => e.currentTarget.style.borderColor = border}
                >
                  <ch.icon size={18} color={ch?.color} style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: text }}>{tmpl.name}</div>
                    {tmpl.subject && (
                      <div style={{ fontSize: 12, color: sub, marginTop: 2 }}>Subject: {tmpl.subject}</div>
                    )}
                    <div style={{
                      fontSize: 12, color: sub, marginTop: 3,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {tmpl.body.slice(0, 90)}{tmpl.body.length > 90 ? '…' : ''}
                    </div>
                  </div>
                  <ChevronRight size={15} color={sub} style={{ flexShrink: 0 }} />
                </button>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <button
              onClick={() => setStep(STEP_CHANNEL)}
              style={{ fontSize: 13, color: sub, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              ← Back
            </button>
          </div>
        </>
      )}

      {/* ── STEP 3: Preview & Send ──────────────────────────────────────────── */}
      {step === STEP_PREVIEW && (
        <>
          {/* Destination info */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
            background: dark ? '#0F172A' : '#F0FDF4',
            border: `1px solid ${dark ? '#166534' : '#BBF7D0'}`, borderRadius: 8, marginBottom: 16,
          }}>
            <Info size={14} color={dark ? '#4ADE80' : '#16A34A'} />
            <span style={{ fontSize: 12, color: dark ? '#4ADE80' : '#15803D' }}>
              Sending via {ch?.label} to:{' '}
              <strong>{channel === 'whatsapp' ? lead.mobile : lead.email}</strong>
            </span>
          </div>

          {/* Template name chip */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
            <LayoutTemplate size={13} color={sub} />
            <span style={{ fontSize: 12, color: sub }}>Template:</span>
            <span style={{
              fontSize: 12, fontWeight: 600, color: ch?.color,
              background: ch ? ch.color + '15' : 'transparent',
              borderRadius: 6, padding: '2px 8px',
            }}>
              {selected?.name}
            </span>
          </div>

          {/* Subject (email) */}
          {channel === 'email' && (preview?.subject || selected?.subject) && (
            <div style={{ marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: sub }}>Subject: </span>
              <span style={{ fontSize: 13, color: text }}>
                {prevLoading ? '…' : preview?.subject || selected?.subject}
              </span>
            </div>
          )}

          {/* Body preview */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: sub, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Message Preview
            </div>
            <div style={{
              background: surfBg, border: `1px solid ${border}`, borderRadius: 10,
              padding: 16, maxHeight: 260, overflowY: 'auto',
            }}>
              {prevLoading ? (
                <div style={{ color: sub, fontSize: 13 }}>Rendering preview…</div>
              ) : (
                <pre style={{
                  margin: 0, fontFamily: 'inherit', fontSize: 13, lineHeight: 1.65,
                  color: dark ? '#CBD5E1' : '#334155', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {preview?.body || selected?.body}
                </pre>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button
              onClick={() => setStep(STEP_TEMPLATE)}
              style={{ fontSize: 13, color: sub, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              ← Change Template
            </button>
            <button
              onClick={handleSend}
              disabled={sending || prevLoading}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 22px', borderRadius: 9, border: 'none',
                background: ch?.color, color: '#fff',
                fontSize: 14, fontWeight: 600,
                cursor: sending ? 'not-allowed' : 'pointer',
                opacity: sending ? 0.75 : 1,
              }}
            >
              <Send size={14} />
              {sending ? 'Sending…' : channel === 'whatsapp' ? 'Send WhatsApp' : 'Send Email'}
            </button>
          </div>
        </>
      )}

      {/* ── STEP 4: Result ──────────────────────────────────────────────────── */}
      {step === STEP_RESULT && result && (
        <div style={{ textAlign: 'center', padding: '10px 0 20px' }}>
          {result.ok ? (
            <>
              <CheckCircle size={48} color="#22C55E" style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 17, fontWeight: 600, color: text, marginBottom: 6 }}>{result.msg}</div>
              <div style={{ fontSize: 13, color: sub }}>
                Message sent to {channel === 'whatsapp' ? lead.mobile : lead.email}
              </div>
            </>
          ) : (
            <>
              {result.code === 'NOT_ON_WHATSAPP'
                ? <AlertTriangle size={48} color="#F59E0B" style={{ marginBottom: 12 }} />
                : <XCircle size={48} color="#EF4444" style={{ marginBottom: 12 }} />}
              <div style={{ fontSize: 17, fontWeight: 600, color: result.code === 'NOT_ON_WHATSAPP' ? '#F59E0B' : '#EF4444', marginBottom: 6 }}>
                {result.code === 'NOT_ON_WHATSAPP' ? 'Number not on WhatsApp' : 'Send Failed'}
              </div>
              <div style={{ fontSize: 13, color: sub }}>{result.msg}</div>
            </>
          )}
          <button
            onClick={handleClose}
            style={{
              marginTop: 24, padding: '10px 28px', borderRadius: 9, border: 'none',
              background: dark ? '#334155' : '#E2E8F0', color: text,
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      )}
    </Modal>
  );
};

export default ConnectLeadModal;
